/**
 * NotificationBell — Cloche de notifications in-app.
 *
 * L13.17.2 — refonte complète. Avant (L13.6), ce n'était pas une cloche :
 * juste un compteur calculé en direct (= nombre de signalements de
 * l'administré), sans état lu/non-lu — la pastille ne partait jamais.
 *
 * Maintenant, branché sur le vrai système de notifications (L13.17.1) :
 *   - la pastille = nombre de notifications NON LUES (`/notifications/unread-count`) ;
 *   - cliquer sur la cloche ouvre un panneau déroulant qui liste les
 *     notifications (`/notifications`) ET les marque toutes lues
 *     (`/notifications/mark-all-read`) → la pastille retombe à 0 ;
 *   - cliquer sur une notification navigue vers le signalement ou l'alerte
 *     concerné, et ferme le panneau.
 *
 * Le compteur se rafraîchit aussi toutes les 60 s (liveness) tant que le
 * panneau est fermé.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { api, type NotificationItem } from '@/lib/api';
import {
  Bell, MapPin, Megaphone, Mail, Vote, Loader2, X,
  Package, GraduationCap, Users, FileText, Trash2, CalendarClock,
  CalendarX, Landmark, Lightbulb, FolderInput, MessageSquareWarning,
  Receipt,
} from 'lucide-react';

/**
 * Présentation (icône + couleur) par type de notification.
 *
 * ⚠️ Corrige un bug d'affichage réel : jusqu'au lot N1, ce mapping était une
 * cascade de ternaires ne couvrant que 4 types sur 16, avec un fallback muet
 * sur MapPin (l'icône « signalement »). Une notification d'urbanisme, de
 * cantine ou de matériel s'affichait donc avec une punaise de signalement —
 * ce qui participe au ressenti « les cloches sont confuses » remonté par la
 * mairie.
 *
 * `Record` complet plutôt que ternaires : TypeScript refuse désormais de
 * compiler si un type du référentiel est ajouté sans sa présentation. Le
 * fallback ne subsiste que pour le cas d'un type serveur inconnu du bundle
 * front déployé (décalage de version).
 */
const PRESENTATION: Record<NotificationItem['type'], { icon: typeof Bell; tone: string }> = {
  signalement_statut:   { icon: MapPin,                tone: 'text-tech-fg' },
  alerte:               { icon: Megaphone,             tone: 'text-urgent' },
  message_interne:      { icon: Mail,                  tone: 'text-grc-fg' },
  materiel_reservation: { icon: Package,               tone: 'text-tech-fg' },
  dossier_scolaire:     { icon: GraduationCap,         tone: 'text-ecole-fg' },
  association:          { icon: Users,                 tone: 'text-asso-fg' },
  referendum:           { icon: Vote,                  tone: 'text-grc-fg' },
  urbanisme:            { icon: FileText,              tone: 'text-urba-fg' },
  dechets_rappel:       { icon: Trash2,                tone: 'text-grc-fg' },
  rdv_rappel:           { icon: CalendarClock,         tone: 'text-grc-fg' },
  rdv_creneaux_vides:   { icon: CalendarX,             tone: 'text-urgent' },
  concession_echeance:  { icon: Landmark,              tone: 'text-grc-fg' },
  concession_demande:   { icon: Landmark,              tone: 'text-grc-fg' },
  idee:                 { icon: Lightbulb,             tone: 'text-grc-fg' },
  collecte_dossier:     { icon: FolderInput,           tone: 'text-grc-fg' },
  sms_solde_bas:        { icon: MessageSquareWarning,  tone: 'text-urgent' },
  // Arbitrage B (20/08) — la cloche du manager N1 : une note de frais de son
  // service attend sa validation, le lien ouvre sa vue perso onglet NDF.
  ndf_a_valider:        { icon: Receipt,               tone: 'text-rh-fg' },
};

function presentation(type: NotificationItem['type']) {
  return PRESENTATION[type] ?? { icon: Bell, tone: 'text-ink-muted' };
}

/** Temps relatif court : « à l'instant », « il y a 12 min », « il y a 3 h ». */
function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.round(diff / 60000);
  if (min < 1) return "à l'instant";
  if (min < 60) return `il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `il y a ${h} h`;
  const j = Math.floor(h / 24);
  return `il y a ${j} j`;
}

const POLL_MS = 60_000;

/**
 * Nom de l'événement de rafraîchissement de la cloche (§5.3 du retour mairie :
 * « rafraîchir la cloche après une action, ne pas attendre le prochain poll »).
 */
const REFRESH_EVENT = 'vpc:notifications-refresh';

/**
 * Demande à la cloche de se rafraîchir immédiatement.
 *
 * À appeler depuis n'importe quel écran après une action qui produit une
 * notification (valider un congés, résoudre une intervention, remettre des
 * clés…). Sans cet appel, la cloche attend son poll de 60 s — c'est le « confort
 * d'affichage » que la mairie relève.
 *
 * ─── POURQUOI UN ÉVÉNEMENT PLUTÔT QU'UN CONTEXTE REACT ──────────────────────
 *
 * Un contexte imposerait de brancher un provider et de faire descendre un
 * `refresh()` dans chaque écran concerné — beaucoup de fichiers touchés, pour du
 * confort. Ici, l'émetteur ne connaît rien de la cloche, et la cloche ne connaît
 * aucun émetteur : une ligne suffit côté appelant, sans couplage.
 *
 * Sans écouteur monté (utilisateur non connecté), l'appel ne fait rien — c'est
 * volontaire, il n'y a rien à rafraîchir.
 */
export function rafraichirNotifications(): void {
  window.dispatchEvent(new CustomEvent(REFRESH_EVENT));
}

export function NotificationBell() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [count, setCount] = useState<number | null>(null);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // --- Compteur de non-lues : au montage + poll 60 s tant que le panneau est fermé.
  const refreshCount = useCallback(async () => {
    try {
      const res = await api.notifications.unreadCount();
      setCount(res.count);
    } catch {
      // Silencieux : un échec ne doit pas casser le topbar.
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    void refreshCount();
    const t = setInterval(() => {
      if (!open) void refreshCount();
    }, POLL_MS);

    // §5.3 — rafraîchissement immédiat après une action, sans attendre le poll.
    const onRefresh = () => { if (!open) void refreshCount(); };
    window.addEventListener(REFRESH_EVENT, onRefresh);

    // Retour sur l'onglet : c'est le cas le plus fréquent en usage réel. Un agent
    // bascule vers autre chose, revient, et doit voir sa cloche à jour tout de
    // suite — pas jusqu'à 60 s plus tard. Couvre aussi le réveil après veille,
    // où le timer a pu ne pas tourner.
    const onVisible = () => {
      if (document.visibilityState === 'visible' && !open) void refreshCount();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(t);
      window.removeEventListener(REFRESH_EVENT, onRefresh);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user, open, refreshCount]);

  // --- Fermeture au clic en dehors du panneau.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // --- Ouverture : charge la liste, puis marque tout lu → pastille à 0.
  async function openPanel() {
    setOpen(true);
    setLoading(true);
    try {
      const res = await api.notifications.list();
      setItems(res.items);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
    try {
      await api.notifications.markAllRead();
      setCount(0);
    } catch {
      // Silencieux : si le marquage échoue, la pastille restera (réessai au prochain poll).
    }
  }

  function handleSelect(n: NotificationItem) {
    setOpen(false);
    if (n.lien) navigate(n.lien);
  }

  async function supprimerUne(id: string) {
    setItems((prev) => prev.filter((n) => n.id !== id)); // optimiste
    try { await api.notifications.remove(id); }
    catch { void openPanel(); } // en cas d'échec, on recharge
  }

  async function toutEffacer() {
    setItems([]);
    setCount(0);
    try { await api.notifications.removeAll(); }
    catch { void openPanel(); }
  }

  const countLabel = count !== null && count > 0 ? (count > 9 ? '9+' : String(count)) : null;

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => (open ? setOpen(false) : void openPanel())}
        aria-label={
          count && count > 0
            ? `Notifications, ${count} non lue${count > 1 ? 's' : ''}`
            : 'Notifications'
        }
        aria-haspopup="true"
        aria-expanded={open}
        className="relative inline-flex h-10 w-10 items-center justify-center rounded-r-lg border border-border bg-surface/60 text-ink-muted backdrop-blur-glass transition-all hover:bg-surface/80 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
      >
        <Bell className="h-4 w-4" aria-hidden="true" />
        {countLabel !== null && (
          <span
            aria-hidden="true"
            className="absolute -right-1 -top-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full border border-bg bg-urgent px-1 text-2xs font-bold tabular-nums text-white"
          >
            {countLabel}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Notifications"
          className="fixed inset-x-3 top-16 z-50 w-auto overflow-hidden rounded-r-2xl border border-border bg-surface/95 shadow-glass-hi backdrop-blur-glass animate-fade-in sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-80 sm:max-w-[calc(100vw-2rem)]"
        >
          <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
            <h2 className="font-display text-sm font-semibold tracking-r-snug text-ink">
              Notifications
            </h2>
            {items.length > 0 && (
              <button
                type="button"
                onClick={toutEffacer}
                className="text-2xs font-medium text-ink-muted underline-offset-2 transition-colors hover:text-urgent hover:underline focus-visible:outline-none"
              >
                Tout effacer
              </button>
            )}
          </div>

          <div className="max-h-[60vh] overflow-y-auto">
            {loading && (
              <div className="flex items-center gap-2 px-4 py-6 text-sm text-ink-muted">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                Chargement…
              </div>
            )}

            {!loading && items.length === 0 && (
              <div className="px-4 py-8 text-center">
                <Bell className="mx-auto h-8 w-8 text-ink-dim" aria-hidden="true" />
                <p className="mt-2 text-sm text-ink-muted">Aucune notification</p>
                <p className="mt-0.5 text-2xs text-ink-subtle">
                  Vous serez prévenu·e quand vos signalements évoluent ou lors
                  d'une alerte de la mairie.
                </p>
              </div>
            )}

            {!loading &&
              items.map((n) => {
                const { icon: Icon, tone } = presentation(n.type);
                return (
                  <div
                    key={n.id}
                    className="flex items-start gap-1 border-b border-border/60 px-2 py-3 transition-colors last:border-b-0 hover:bg-surface"
                  >
                    <button
                      type="button"
                      onClick={() => handleSelect(n)}
                      disabled={!n.lien}
                      className="flex min-w-0 flex-1 items-start gap-2.5 px-2 text-left focus-visible:outline-none disabled:cursor-default"
                    >
                      <span
                        className={`mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-r-md border border-border bg-bg-2/60 ${tone}`}
                        aria-hidden="true"
                      >
                        <Icon className="h-3.5 w-3.5" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-start gap-1.5">
                          {!n.lu && (
                            <span
                              className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-info"
                              aria-label="Non lue"
                            />
                          )}
                          <span className="font-medium text-sm text-ink line-clamp-2">
                            {n.titre}
                          </span>
                        </span>
                        {n.corps && (
                          <span className="mt-0.5 block text-xs text-ink-muted line-clamp-2">
                            {n.corps}
                          </span>
                        )}
                        <span className="mt-0.5 block text-2xs text-ink-subtle">
                          {relativeTime(n.createdAt)}
                        </span>
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => supprimerUne(n.id)}
                      aria-label="Supprimer cette notification"
                      className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-r-md text-ink-subtle transition-colors hover:bg-urgent/10 hover:text-urgent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-urgent"
                    >
                      <X className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}
